import { useState } from "react";
import { Link } from "react-router-dom";
import {
  adminStats,
  adminLeads,
  adminSubmissions,
  adminExperiences,
  moderateExperience,
  archiveLead,
  adminVideoStats,
  adminCreateMember,
  adminMembers,
} from "@/lib/api";
import { Logo } from "@/components/Brand";
import { toast } from "sonner";

const LEAD_TABS = [
  { key: "", label: "All leads" },
  { key: "consultation", label: "Consultations" },
  { key: "full_results", label: "Result requests" },
  { key: "book", label: "Book" },
  { key: "video_series", label: "Videos" },
  { key: "zoom", label: "Zoom" },
];

const STATUS_STYLES = {
  pending: "bg-amber-100 text-amber-800",
  published: "bg-[#EAF2EC] text-[#4A7C59]",
  rejected: "bg-stone-200 text-stone-500",
};

export default function Admin() {
  const [key, setKey] = useState(sessionStorage.getItem("tenet_admin_key") || "");
  const [authed, setAuthed] = useState(false);
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [experiences, setExperiences] = useState([]);
  const [videoStats, setVideoStats] = useState([]);
  const [members, setMembers] = useState([]);
  const [memberForm, setMemberForm] = useState({ name: "", email: "", password: "" });
  const [createdMember, setCreatedMember] = useState(null);
  const [view, setView] = useState("leads");
  const [tab, setTab] = useState("");
  const [archivedView, setArchivedView] = useState(false);
  const [loading, setLoading] = useState(false);

  const load = async (k, leadType = "", archived = false) => {
    setLoading(true);
    try {
      const [s, l, subs, exps, vstats, mems] = await Promise.all([
        adminStats(k),
        adminLeads(k, leadType, archived),
        adminSubmissions(k),
        adminExperiences(k),
        adminVideoStats(k),
        adminMembers(k),
      ]);
      setStats(s);
      setLeads(l.leads);
      setSubmissions(subs.submissions);
      setExperiences(exps.experiences);
      setVideoStats(vstats.video_stats);
      setMembers(mems.members);
      setAuthed(true);
      sessionStorage.setItem("tenet_admin_key", k);
    } catch (e) {
      toast.error("Invalid admin key.");
      setAuthed(false);
    } finally {
      setLoading(false);
    }
  };

  const switchTab = async (t) => {
    setTab(t);
    setView("leads");
    const l = await adminLeads(key, t, archivedView);
    setLeads(l.leads);
  };

  const toggleArchivedView = async (archived) => {
    setArchivedView(archived);
    const l = await adminLeads(key, tab, archived);
    setLeads(l.leads);
  };

  const setArchived = async (id, archived) => {
    try {
      await archiveLead(key, id, archived);
      const [l, s] = await Promise.all([adminLeads(key, tab, archivedView), adminStats(key)]);
      setLeads(l.leads);
      setStats(s);
      toast.success(archived ? "Lead archived." : "Lead restored.");
    } catch {
      toast.error("Couldn't update that lead.");
    }
  };

  const moderate = async (id, status) => {    try {
      await moderateExperience(key, id, status);
      const [exps, s] = await Promise.all([adminExperiences(key), adminStats(key)]);
      setExperiences(exps.experiences);
      setStats(s);
      toast.success(status === "published" ? "Experience published." : `Experience marked ${status}.`);
    } catch {
      toast.error("Couldn't update that experience.");
    }
  };

  const createMember = async (e) => {
    e.preventDefault();
    if (!memberForm.name.trim() || !memberForm.email.trim()) return toast.error("Name and email are required.");
    try {
      const res = await adminCreateMember(key, memberForm);
      setCreatedMember(res);
      setMemberForm({ name: "", email: "", password: "" });
      const m = await adminMembers(key);
      setMembers(m.members);
      toast.success("Member created — welcome email sent.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't create that member.");
    }
  };

  if (!authed) {
    return (
      <div className="min-h-screen grid place-items-center bg-[#FDFBF7] px-4">
        <div className="w-full max-w-sm bg-white rounded-2xl border border-stone-200 p-8 shadow-sm">
          <div className="flex justify-center mb-6"><Logo /></div>
          <h1 className="font-playfair text-2xl text-stone-900 text-center">Admin dashboard</h1>
          <p className="text-sm text-stone-500 text-center mt-1">Enter your admin key to continue.</p>
          <input
            data-testid="admin-key-input"
            type="password"
            value={key}
            onChange={(e) => setKey(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load(key)}
            placeholder="Admin key"
            className="w-full mt-6 rounded-xl border border-stone-300 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1F3D2E]/40"
          />
          <button
            data-testid="admin-login-button"
            onClick={() => load(key)}
            disabled={loading}
            className="btn-terracotta w-full rounded-full px-6 py-3 text-sm font-semibold mt-4 disabled:opacity-60"
          >
            {loading ? "Checking\u2026" : "Enter dashboard"}
          </button>
          <Link to="/" className="block text-center text-xs text-stone-400 mt-5 hover:text-stone-600">Back to site</Link>
        </div>
      </div>
    );
  }

  const statCards = stats
    ? [
        { label: "Assessments taken", value: stats.assessment_submissions, icon: "fa-clipboard-list" },
        { label: "Consultations", value: stats.consultations, icon: "fa-calendar-check" },
        { label: "Result emails", value: stats.result_emails, icon: "fa-envelope" },
        { label: "Book requests", value: stats.book_requests, icon: "fa-book" },
        { label: "Zoom registrants", value: stats.zoom_registrations, icon: "fa-video" },
        { label: "Experiences to review", value: stats.experiences_pending, icon: "fa-comment-dots" },
      ]
    : [];

  const VIEWS = [
    { key: "leads", label: "Leads" },
    { key: "submissions", label: "Assessment submissions" },
    { key: "experiences", label: "Client experiences" },
    { key: "videos", label: "Video views" },
    { key: "members", label: "Members" },
  ];

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <header className="border-b border-stone-200 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-4 flex items-center justify-between">
          <Logo />
          <div className="flex items-center gap-4">
            <Link to="/" className="text-sm text-stone-500 hover:text-stone-800">View site</Link>
            <button
              data-testid="admin-logout"
              onClick={() => { sessionStorage.removeItem("tenet_admin_key"); setKey(""); setAuthed(false); }}
              className="text-sm font-semibold text-[#1F3D2E]"
            >
              Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-10">
        <h1 className="font-playfair text-3xl text-stone-900">Leads, assessments & experiences</h1>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mt-8" data-testid="admin-stats">
          {statCards.map((c) => (
            <div key={c.label} className="bg-white rounded-2xl border border-stone-200 p-5">
              <i className={`fa-solid ${c.icon} text-[#1F3D2E]`} />
              <p className="font-playfair text-3xl text-stone-900 mt-3">{c.value}</p>
              <p className="text-xs text-stone-500 mt-1">{c.label}</p>
            </div>
          ))}
        </div>

        <div className="flex gap-4 mt-10 border-b border-stone-200">
          {VIEWS.map((v) => (
            <button
              key={v.key}
              onClick={() => setView(v.key)}
              data-testid={`admin-view-${v.key}`}
              className={`pb-3 text-sm font-semibold ${view === v.key ? "text-[#1F3D2E] border-b-2 border-[#1F3D2E]" : "text-stone-500"}`}
            >
              {v.label}
            </button>
          ))}
        </div>

        {view === "leads" && (
          <>
            <div className="flex flex-wrap items-center justify-between gap-3 mt-6">
              <div className="flex flex-wrap gap-2">
                {LEAD_TABS.map((t) => (
                  <button
                    key={t.key}
                    onClick={() => switchTab(t.key)}
                    data-testid={`admin-tab-${t.key || "all"}`}
                    className={`rounded-full px-4 py-2 text-xs font-medium border ${
                      tab === t.key ? "bg-[#1F3D2E] text-white border-[#1F3D2E]" : "bg-white text-stone-600 border-stone-300"
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
              <div className="inline-flex rounded-full border border-stone-300 bg-white p-1">
                <button
                  onClick={() => toggleArchivedView(false)}
                  data-testid="admin-leads-active"
                  className={`rounded-full px-4 py-1.5 text-xs font-semibold ${!archivedView ? "bg-[#1F3D2E] text-white" : "text-stone-500"}`}
                >
                  Active
                </button>
                <button
                  onClick={() => toggleArchivedView(true)}
                  data-testid="admin-leads-archived"
                  className={`rounded-full px-4 py-1.5 text-xs font-semibold ${archivedView ? "bg-[#1F3D2E] text-white" : "text-stone-500"}`}
                >
                  Archived
                </button>
              </div>
            </div>
            <div className="mt-6 overflow-x-auto bg-white rounded-2xl border border-stone-200">
              <table className="w-full text-sm" data-testid="admin-leads-table">
                <thead className="bg-stone-50 text-stone-500 text-left">
                  <tr>
                    <th className="px-4 py-3 font-medium">Type</th>
                    <th className="px-4 py-3 font-medium">Name</th>
                    <th className="px-4 py-3 font-medium">Email</th>
                    <th className="px-4 py-3 font-medium">Detail</th>
                    <th className="px-4 py-3 font-medium">IP</th>
                    <th className="px-4 py-3 font-medium">When</th>
                    <th className="px-4 py-3 font-medium text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map((l) => (
                    <tr key={l.id} className="border-t border-stone-100">
                      <td className="px-4 py-3"><span className="text-xs font-mono uppercase text-amber-800/80">{l.type}</span></td>
                      <td className="px-4 py-3 text-stone-800">{l.name}</td>
                      <td className="px-4 py-3 text-stone-600">{l.email}</td>
                      <td className="px-4 py-3 text-stone-500">{l.assessment_title || l.topic || l.audience || "\u2014"}</td>
                      <td className="px-4 py-3 text-stone-500 font-mono text-xs">{l.ip || "\u2014"}</td>
                      <td className="px-4 py-3 text-stone-400 whitespace-nowrap">{new Date(l.created_at).toLocaleString()}</td>
                      <td className="px-4 py-3 text-right">
                        {archivedView ? (
                          <button
                            data-testid={`admin-restore-${l.id}`}
                            onClick={() => setArchived(l.id, false)}
                            className="rounded-full border border-stone-300 text-stone-600 px-3 py-1.5 text-xs font-semibold hover:bg-stone-50"
                          >
                            Restore
                          </button>
                        ) : (
                          <button
                            data-testid={`admin-archive-${l.id}`}
                            onClick={() => setArchived(l.id, true)}
                            className="rounded-full border border-stone-300 text-stone-600 px-3 py-1.5 text-xs font-semibold hover:bg-stone-50"
                          >
                            Archive
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                  {leads.length === 0 && (
                    <tr><td colSpan={7} className="px-4 py-8 text-center text-stone-400">{archivedView ? "No archived leads." : "No leads yet."}</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}

        {view === "submissions" && (
          <div className="mt-6 overflow-x-auto bg-white rounded-2xl border border-stone-200">
            <table className="w-full text-sm" data-testid="admin-submissions-table">
              <thead className="bg-stone-50 text-stone-500 text-left">
                <tr>
                  <th className="px-4 py-3 font-medium">Assessment</th>
                  <th className="px-4 py-3 font-medium">Email</th>
                  <th className="px-4 py-3 font-medium">Result</th>
                  <th className="px-4 py-3 font-medium">Score</th>
                  <th className="px-4 py-3 font-medium">Emailed</th>
                  <th className="px-4 py-3 font-medium">IP</th>
                  <th className="px-4 py-3 font-medium">When</th>
                </tr>
              </thead>
              <tbody>
                {submissions.map((s) => (
                  <tr key={s.id} className="border-t border-stone-100">
                    <td className="px-4 py-3 text-stone-800">{s.assessment_title}</td>
                    <td className="px-4 py-3 text-stone-600">{s.email || "\u2014"}</td>
                    <td className="px-4 py-3 text-stone-600">{s.band_label}</td>
                    <td className="px-4 py-3 text-stone-500">{s.score}/{s.max_score}</td>
                    <td className="px-4 py-3">{s.emailed ? <i className="fa-solid fa-check text-[#4A7C59]" /> : <span className="text-stone-300">&mdash;</span>}</td>
                    <td className="px-4 py-3 text-stone-500 font-mono text-xs">{s.ip || "\u2014"}</td>
                    <td className="px-4 py-3 text-stone-400 whitespace-nowrap">{new Date(s.created_at).toLocaleString()}</td>
                  </tr>
                ))}
                {submissions.length === 0 && (
                  <tr><td colSpan={7} className="px-4 py-8 text-center text-stone-400">No submissions yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {view === "experiences" && (
          <div className="mt-6 space-y-4" data-testid="admin-experiences">
            {experiences.map((e) => (
              <div key={e.id} className="bg-white rounded-2xl border border-stone-200 p-5">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    {e.photo ? (
                      <img src={e.photo} alt={e.display_name} className="h-10 w-10 rounded-full object-cover" />
                    ) : (
                      <span className="grid h-10 w-10 place-items-center rounded-full bg-[#F0EBE0] text-[#8B6F3D] font-playfair">
                        {e.display_name?.[0] || "\u2022"}
                      </span>
                    )}
                    <div>
                      <p className="font-playfair text-stone-900">{e.display_name}</p>
                      <p className="text-xs text-stone-400">
                        {e.featured ? "Featured" : "Submitted"} · {new Date(e.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <span className={`text-xs font-semibold px-3 py-1 rounded-full ${STATUS_STYLES[e.status] || "bg-stone-100 text-stone-500"}`}>
                    {e.status}
                  </span>
                </div>
                <p className="text-sm text-stone-600 mt-4 whitespace-pre-line leading-relaxed">{e.experience}</p>
                {e.advice && <p className="text-sm text-stone-500 italic mt-2">{e.advice}</p>}
                {!e.featured && (
                  <div className="flex gap-2 mt-4">
                    {e.status !== "published" && (
                      <button
                        data-testid={`admin-publish-${e.id}`}
                        onClick={() => moderate(e.id, "published")}
                        className="rounded-full bg-[#1F3D2E] text-white px-4 py-2 text-xs font-semibold"
                      >
                        Publish
                      </button>
                    )}
                    {e.status !== "rejected" && (
                      <button
                        data-testid={`admin-reject-${e.id}`}
                        onClick={() => moderate(e.id, "rejected")}
                        className="rounded-full border border-stone-300 text-stone-600 px-4 py-2 text-xs font-semibold"
                      >
                        Reject
                      </button>
                    )}
                    {e.status !== "pending" && (
                      <button
                        onClick={() => moderate(e.id, "pending")}
                        className="rounded-full border border-stone-300 text-stone-500 px-4 py-2 text-xs font-semibold"
                      >
                        Move to pending
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))}
            {experiences.length === 0 && (
              <div className="bg-white rounded-2xl border border-stone-200 px-4 py-8 text-center text-stone-400">
                No experiences yet.
              </div>
            )}
          </div>
        )}

        {view === "videos" && (
          <div className="mt-6 overflow-x-auto bg-white rounded-2xl border border-stone-200">
            <table className="w-full text-sm" data-testid="admin-video-stats-table">
              <thead className="bg-stone-50 text-stone-500 text-left">
                <tr>
                  <th className="px-4 py-3 font-medium">Chapter</th>
                  <th className="px-4 py-3 font-medium">Title</th>
                  <th className="px-4 py-3 font-medium text-right">Watches</th>
                  <th className="px-4 py-3 font-medium text-right">Downloads</th>
                </tr>
              </thead>
              <tbody>
                {videoStats.map((v) => (
                  <tr key={v.id} className="border-t border-stone-100" data-testid={`admin-video-row-${v.id}`}>
                    <td className="px-4 py-3 text-stone-500">Chapter {v.number}</td>
                    <td className="px-4 py-3 text-stone-800">{v.title}</td>
                    <td className="px-4 py-3 text-right text-stone-700 font-semibold" data-testid={`admin-video-watch-${v.id}`}>{v.watch}</td>
                    <td className="px-4 py-3 text-right text-stone-700 font-semibold" data-testid={`admin-video-download-${v.id}`}>{v.download}</td>
                  </tr>
                ))}
                {videoStats.length === 0 && (
                  <tr><td colSpan={4} className="px-4 py-8 text-center text-stone-400">No video activity yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {view === "members" && (
          <div className="mt-6 grid lg:grid-cols-3 gap-6" data-testid="admin-members">
            <div className="lg:col-span-1 bg-white rounded-2xl border border-stone-200 p-5">
              <h3 className="font-playfair text-lg text-stone-900">Add a member</h3>
              <p className="text-xs text-stone-500 mt-1">They'll get a welcome email with their password. Leave password blank to auto-generate one.</p>
              <form onSubmit={createMember} className="space-y-3 mt-4" data-testid="admin-member-form">
                <input data-testid="admin-member-name" value={memberForm.name} onChange={(e) => setMemberForm({ ...memberForm, name: e.target.value })} placeholder="Full name" className="w-full rounded-xl border border-stone-300 px-4 py-2.5 text-sm" />
                <input data-testid="admin-member-email" type="email" value={memberForm.email} onChange={(e) => setMemberForm({ ...memberForm, email: e.target.value })} placeholder="Email address" className="w-full rounded-xl border border-stone-300 px-4 py-2.5 text-sm" />
                <input data-testid="admin-member-password" value={memberForm.password} onChange={(e) => setMemberForm({ ...memberForm, password: e.target.value })} placeholder="Password (optional)" className="w-full rounded-xl border border-stone-300 px-4 py-2.5 text-sm" />
                <button type="submit" data-testid="admin-member-create" className="btn-terracotta w-full rounded-full px-5 py-2.5 text-sm font-semibold">Create &amp; email access</button>
              </form>
              {createdMember && (
                <div className="mt-4 rounded-xl bg-[#EEF2ED] border border-[#1F3D2E]/15 p-4 text-sm" data-testid="admin-member-created">
                  <p className="text-stone-700"><strong>{createdMember.email}</strong> created.</p>
                  <p className="text-stone-600 mt-1">Password: <span className="font-mono">{createdMember.password}</span></p>
                  <p className="text-xs text-stone-400 mt-1">Welcome email sent to the member.</p>
                </div>
              )}
            </div>
            <div className="lg:col-span-2 overflow-x-auto bg-white rounded-2xl border border-stone-200">
              <table className="w-full text-sm" data-testid="admin-members-table">
                <thead className="bg-stone-50 text-stone-500 text-left">
                  <tr>
                    <th className="px-4 py-3 font-medium">Name</th>
                    <th className="px-4 py-3 font-medium">Email</th>
                    <th className="px-4 py-3 font-medium">Progress</th>
                    <th className="px-4 py-3 font-medium">Joined</th>
                  </tr>
                </thead>
                <tbody>
                  {members.map((mem) => (
                    <tr key={mem.id} className="border-t border-stone-100" data-testid={`admin-member-row-${mem.email}`}>
                      <td className="px-4 py-3 text-stone-800">{mem.name}</td>
                      <td className="px-4 py-3 text-stone-600">{mem.email}</td>
                      <td className="px-4 py-3 text-stone-500">{mem.completed_count} / 8</td>
                      <td className="px-4 py-3 text-stone-400 whitespace-nowrap">{new Date(mem.created_at).toLocaleDateString()}</td>
                    </tr>
                  ))}
                  {members.length === 0 && (
                    <tr><td colSpan={4} className="px-4 py-8 text-center text-stone-400">No members yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
